// src/components/Header/Header.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import AddPropertyModel from "./AddPropertyModel";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./AddPropertyModel.css";
import "./Header.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL = "/api";
// OR try without port if your backend serves both API and frontend:
// const API_BASE_URL = window.location.origin; // This will use the same origin as frontend

// ✅ FIX: Enhanced axios instance with CORS handling
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
  // Add withCredentials if needed for cookies/auth
  // withCredentials: true,
});

// ✅ FIX: Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log(
      `🚀 Making ${config.method?.toUpperCase()} request to:`,
      config.url
    );
    console.log("Full config:", config);
    return config;
  },
  (error) => {
    console.error("❌ Request error:", error);
    return Promise.reject(error);
  }
);

// ✅ FIX: Add response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    console.error("❌ Response error:", {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPropertyPage = location.pathname === "/properties";

  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState({});
  const [userId] = useState(1);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Map of nav items to section class names
  const sectionMap = useRef({
    home: ".hero, .top-header, main", // Multiple selectors for home
    about: ".about",
    properties: ".properties",
    agents: ".agents-section",
    blog: ".blog",
    contact: ".contact",
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 5);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Function to find section by class name
  const findSectionByClass = (className) => {
    // Try multiple selectors
    const selectors = className.split(",").map((s) => s.trim());

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`Found section with selector: ${selector}`);
        return element;
      }
    }

    // If not found with direct selector, try to find by text content
    console.log(`No element found with selectors: ${selectors.join(", ")}`);
    return null;
  };

  // Function to perform the actual scrolling
  const performScroll = (element, sectionKey) => {
    // Added sectionKey parameter
    const header = document.querySelector(".header");
    const headerHeight = header ? header.offsetHeight : 80;

    // Calculate position
    const elementTop = element.offsetTop;
    const scrollPosition = elementTop - headerHeight;

    console.log(
      `Scrolling to position: ${scrollPosition}, element top: ${elementTop}, header height: ${headerHeight}`
    );

    // Smooth scroll
    window.scrollTo({
      top: Math.max(0, scrollPosition), // Ensure not negative
      behavior: "smooth",
    });

    // Update URL without reloading
    window.history.pushState(null, null, `#${sectionKey}`);

    return true;
  };

  // Function to scroll to a section with smooth animation
  const scrollToSection = (sectionKey) => {
    const selectors = sectionMap.current[sectionKey];
    if (!selectors) {
      console.log(`No selectors defined for section: ${sectionKey}`);
      return false;
    }

    const element = findSectionByClass(selectors);
    if (!element) {
      console.log(
        `Could not find element for section: ${sectionKey} with selectors: ${selectors}`
      );

      // Try to find by section title text as fallback
      const allSections = document.querySelectorAll("section");
      for (const section of allSections) {
        const heading = section.querySelector("h1, h2, h3, .section-title");
        if (
          heading &&
          heading.textContent.toLowerCase().includes(sectionKey.toLowerCase())
        ) {
          console.log(`Found section by heading text: ${heading.textContent}`);
          return performScroll(section, sectionKey);
        }
      }

      return false;
    }

    return performScroll(element, sectionKey);
  };

  // Handle navigation click
  const handleNavClick = (e, sectionKey) => {
    e.preventDefault();
    setIsMenuOpen(false);

    console.log(`Clicked ${sectionKey}, current page: ${location.pathname}`);

    // If we're on home page, try to scroll
    if (location.pathname === "/") {
      // Small delay to ensure any state updates are processed
      setTimeout(() => {
        // First, try to find and scroll
        let scrolled = scrollToSection(sectionKey);

        if (!scrolled) {
          console.log(
            `Section ${sectionKey} not found immediately, trying again...`
          );

          // Try again with different delays
          const retryDelays = [100, 200, 500];
          let retryCount = 0;

          const retryScroll = () => {
            if (retryCount < retryDelays.length) {
              setTimeout(() => {
                console.log(`Retry ${retryCount + 1} for ${sectionKey}`);
                scrolled = scrollToSection(sectionKey);
                if (!scrolled && retryCount < retryDelays.length - 1) {
                  retryCount++;
                  retryScroll();
                }
              }, retryDelays[retryCount]);
              retryCount++;
            }
          };

          retryScroll();
        }
      }, 50);
    } else {
      // Navigate to home page with hash
      console.log(`Navigating to home page with section: ${sectionKey}`);
      navigate(`/#${sectionKey}`);
    }
  };

  // Check for hash in URL on page load
  useEffect(() => {
    const handleHashScroll = () => {
      if (location.pathname === "/" && location.hash) {
        const sectionKey = location.hash.substring(1); // Remove #
        console.log(`URL has hash: ${sectionKey}, scrolling to it...`);

        // Wait for page to fully render
        setTimeout(() => {
          scrollToSection(sectionKey);
        }, 800);
      }
    };

    // Initial check
    handleHashScroll();

    // Listen for popstate (back/forward navigation)
    window.addEventListener("popstate", handleHashScroll);

    return () => {
      window.removeEventListener("popstate", handleHashScroll);
    };
  }, [location]);

  // Add debug function to check sections on home page
  const debugSections = () => {
    if (location.pathname === "/") {
      console.log("=== DEBUG: Checking available sections ===");

      // Check by class names from your image
      const sectionClasses = [
        "top-header",
        "hero",
        "hero-search-hero",
        "about",
        "categories-wrapper",
        "testimonials",
        "properties",
        "agents-section",
        "blog",
        "contact",
      ];

      sectionClasses.forEach((className) => {
        const elements = document.querySelectorAll(`.${className}`);
        console.log(`Class .${className}: Found ${elements.length} elements`);
        elements.forEach((el, idx) => {
          console.log(
            `  ${idx + 1}. Tag: ${el.tagName}, Text sample:`,
            el.textContent.substring(0, 100) + "..."
          );
        });
      });

      // Check all sections
      const allSections = document.querySelectorAll("section");
      console.log(`Total sections found: ${allSections.length}`);
      allSections.forEach((section, idx) => {
        console.log(`Section ${idx + 1}:`, {
          className: section.className,
          id: section.id,
          tagName: section.tagName,
          text: section.textContent.substring(0, 50) + "...",
        });
      });

      console.log("=== DEBUG END ===");
    }
  };

  useEffect(() => {
    fetchPropertyOptions();
    fetchUserProperties();
  }, [userId]);

  const fetchPropertyOptions = async () => {
    try {
      // ✅ CHANGED: Use relative URL instead of direct backend URL
      const response = await api.get("/property-options");
      // OLD: "http://72.61.255.137:3001/api/property-options"
      setPropertyOptions(response.data);
    } catch (error) {
      console.error("Error fetching property options:", error);
      toast.error("Failed to load property options. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const fetchUserProperties = async () => {
    try {
      // ✅ CHANGED: Use relative URL instead of direct backend URL
      const response = await api.get(`/user-properties/${userId}`);
      // OLD: `http://72.61.255.137:3001/api/user-properties/${userId}`
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching user properties:", error);
    }
  };

  const handleAddProperty = async (propertyData) => {
    if (isSubmitting) {
      console.log("Already submitting, please wait...");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      Object.keys(propertyData).forEach((key) => {
        if (key !== "images") {
          formData.append(key, propertyData[key]);
        }
      });

      if (propertyData.images) {
        propertyData.images.forEach((image) => {
          formData.append("images", image);
        });
      }

      formData.append("userId", userId);

      // ✅ CHANGED: Use relative URL instead of direct backend URL
      const response = await api.post("/properties", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      // OLD: "http://72.61.255.137:3001/api/properties"

      toast.success(
        "Your property submitted successfully. We will get back to you shortly.",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        }
      );

      setShowModal(false);
      fetchUserProperties();
    } catch (error) {
      if (error.response?.status === 409) {
        setAlert({
          show: true,
          message: "This property already exists!",
          type: "error",
        });
      } else {
        toast.error(
          "Error adding property: " +
            (error.response?.data?.error || error.message),
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <header
      className={`header ${isScrolled ? "scrolled" : ""} ${
        isPropertyPage ? "property-header-fixed" : ""
      }`}
    >
      <div className="container">
        <div className="header-content">
          <div className="logo">
            <Link to="/" className="logo-link">
              <h2>ATLAS SPACES</h2>
              <span>REAL ESTATE AGENCY</span>
            </Link>
          </div>
          <nav className={`nav ${isMenuOpen ? "nav-open" : ""}`}>
            <ul className="nav-list">
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={(e) => handleNavClick(e, "home")}
                >
                  HOME
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={(e) => handleNavClick(e, "about")}
                >
                  ABOUT
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={(e) => handleNavClick(e, "properties")}
                >
                  PROPERTIES
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={(e) => handleNavClick(e, "agents")}
                >
                  AGENTS
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={(e) => handleNavClick(e, "blog")}
                >
                  BLOG
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link"
                  onClick={(e) => handleNavClick(e, "contact")}
                >
                  CONTACT
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowModal(true);
                  }}
                  className="nav-link btn-submit"
                >
                  SUBMIT A PROPERTY
                </a>
              </li>
            </ul>
          </nav>
          <div className="mobile-menu" onClick={toggleMenu}>
            <i className="fas fa-bars"></i>
          </div>
        </div>
      </div>
      <AddPropertyModel
        show={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        onSubmit={handleAddProperty}
        propertyOptions={propertyOptions}
        isSubmitting={isSubmitting}
      />
      <ToastContainer />
    </header>
  );
};

export default Header;
